import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowRight, 
  Target, 
  Lightbulb, 
  TrendingUp, 
  Layers,
  AlertTriangle,
  CircleDot,
  Sparkles,
  Users
} from 'lucide-react';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6, ease: 'easeOut' }
  }
};

const challengeItems = [
  {
    icon: AlertTriangle,
    title: 'Resource Leakage',
    description: 'Inefficiencies that drain critical funding before it reaches the frontline.'
  },
  {
    icon: TrendingUp,
    title: 'Stagnant Indices',
    description: 'Development plateaus that incrementalism cannot break.'
  },
  {
    icon: Layers,
    title: 'Systemic Fragility',
    description: 'A lack of the "Cognitive Infrastructure" required to absorb shocks or adopt innovation.'
  }
];

const impactPillars = [
  {
    icon: Target,
    title: 'Strategic Divergence',
    description: 'Moving away from low-impact, contested policy spaces toward high-yield "Unserved Frontiers."'
  },
  {
    icon: Lightbulb,
    title: 'Value Innovation',
    description: 'Decoupling health excellence from exponential spending.'
  },
  {
    icon: Users,
    title: 'Leadership by Design',
    description: 'Transitioning from "Accidental Managers" to "Systemic Architects."'
  }
];

export const HomePage = () => {
  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center bg-gradient-to-br from-slate-50 to-teal-50" data-testid="hero-section">
        {/* Background Image - Black African Professionals */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1560250097-0b93528c311a?w=1200&q=60"
            alt="Black African Business Professional"
            className="w-full h-full object-cover opacity-20"
            loading="eager"
            decoding="async"
            fetchpriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-white/90 via-white/70 to-transparent" />
        </div>

        {/* Hero Content */}
        <div className="container-main relative z-10 pt-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              <motion.span 
                variants={itemVariants}
                className="font-mono text-xs uppercase tracking-widest text-teal-600 mb-6 block"
              >
                Health & Social Development Consulting
              </motion.span>
              
              <motion.h1 
                variants={itemVariants}
                className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-[1.1] mb-6"
              >
                Transitioning Global Health from{' '}
                <span className="text-slate-400">Default</span> to{' '}
                <span className="text-teal-600">Design</span>.
              </motion.h1>
              
              <motion.p 
                variants={itemVariants}
                className="text-lg md:text-xl text-slate-600 leading-relaxed mb-10"
              >
                HSL is a premier professional services firm dedicated to architecting 
                resilient health systems. We empower global health stakeholders to master the 
                present while engineering the future through the{' '}
                <span className="text-teal-600 font-semibold">HSL Impact Model</span>.
              </motion.p>
              
              <motion.div 
                variants={itemVariants}
                className="flex flex-col sm:flex-row gap-4"
              >
                <Link 
                  to="/contact" 
                  className="btn-primary inline-flex items-center justify-center gap-2 group"
                  data-testid="hero-cta-partner"
                >
                  Partner With Us
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link 
                  to="/methodology" 
                  className="btn-secondary inline-flex items-center justify-center gap-2"
                  data-testid="hero-cta-methodology"
                >
                  Explore Our Methodology
                </Link>
              </motion.div>
            </motion.div>

            {/* Hero Image */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="hidden lg:block"
            >
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1589156280159-27698a70f29e?w=600&q=75"
                  alt="Black African Business Professional"
                  className="w-full h-auto rounded-lg shadow-2xl"
                  loading="eager"
                  decoding="async"
                  fetchpriority="high"
                />
                <div className="absolute -bottom-6 -left-6 bg-teal-600 text-white p-6 shadow-lg">
                  <span className="font-heading text-2xl font-bold block">Design-Led</span>
                  <span className="text-teal-100 text-sm">Health Strategy</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Challenge Section */}
      <section className="section-padding bg-white" data-testid="challenge-section">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mb-16"
          >
            <span className="font-mono text-xs uppercase tracking-widest text-slate-400 mb-4 block">
              The Challenge
            </span>
            <h2 className="font-heading text-3xl md:text-5xl font-semibold text-slate-900 mb-6">
              The Era of Accidental Leadership
            </h2>
            <p className="text-slate-600 text-lg leading-relaxed">
              In many regions, health systems operate in a chronic state of "Default." 
              Outcomes are not the result of intentional strategy, but the accidental 
              byproduct of legacy infrastructure, fragmented donor cycles, and reactive 
              crisis management.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {challengeItems.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-slate-50 border border-slate-200 p-8 hover:border-teal-500 hover:shadow-lg transition-all duration-300 group"
                data-testid={`challenge-card-${index}`}
              >
                <div className="w-12 h-12 bg-white border border-slate-200 flex items-center justify-center mb-6 group-hover:border-teal-500 group-hover:bg-teal-50 transition-colors">
                  <item.icon size={24} className="text-slate-500 group-hover:text-teal-600 transition-colors" />
                </div>
                <h3 className="font-heading text-xl text-slate-900 mb-3">{item.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="section-padding bg-slate-50" data-testid="solution-section">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="font-mono text-xs uppercase tracking-widest text-teal-600 mb-4 block">
                The HSL Solution
              </span>
              <h2 className="font-heading text-3xl md:text-5xl font-semibold text-slate-900 mb-6">
                Designed Excellence
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-8">
                We don't just solve problems; we re-design the landscape. HSL 
                facilitates a strategic pivot from reactive management to Designed Excellence. 
                By applying our <span className="text-teal-600 font-semibold">Ambidextrous Strategy</span>, 
                we enable your organization to perform and transform simultaneously.
              </p>
              <Link 
                to="/methodology"
                className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 font-medium transition-colors group"
                data-testid="solution-learn-more"
              >
                Learn about our methodology
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>

            {/* Ambidextrous Framework */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="grid grid-cols-2 gap-4"
              data-testid="ambidextrous-framework"
            >
              <div className="col-span-2 bg-white border border-slate-200 p-6 shadow-sm">
                <h3 className="font-heading text-lg text-slate-900 mb-2 text-center">
                  The Ambidextrous Framework
                </h3>
              </div>
              
              <div className="bg-white border border-slate-200 p-6 hover:border-teal-500 hover:shadow-md transition-all group">
                <div className="w-10 h-10 bg-slate-100 flex items-center justify-center mb-4 group-hover:bg-teal-100 transition-colors">
                  <CircleDot size={20} className="text-slate-500 group-hover:text-teal-600 transition-colors" />
                </div>
                <h4 className="font-mono text-xs uppercase tracking-widest text-slate-400 mb-2">
                  Exploitation
                </h4>
                <p className="font-heading text-slate-900 text-lg mb-2">Mastering the Present</p>
                <p className="text-slate-500 text-sm">
                  We optimize your current operations, focusing on the Marginal Utility of every dollar spent.
                </p>
              </div>

              <div className="bg-white border border-teal-200 p-6 hover:border-teal-500 hover:shadow-md transition-all group bg-gradient-to-br from-white to-teal-50">
                <div className="w-10 h-10 bg-teal-100 flex items-center justify-center mb-4 group-hover:bg-teal-200 transition-colors">
                  <Sparkles size={20} className="text-teal-600" />
                </div>
                <h4 className="font-mono text-xs uppercase tracking-widest text-teal-600 mb-2">
                  Exploration
                </h4>
                <p className="font-heading text-slate-900 text-lg mb-2">Engineering the Future</p>
                <p className="text-slate-600 text-sm">
                  We architect the innovative platforms of tomorrow—leveraging Systemic Leapfrogging.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Impact Pillars Section */}
      <section className="section-padding bg-white" data-testid="impact-pillars-section">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <span className="font-mono text-xs uppercase tracking-widest text-slate-400 mb-4 block">
              Our Impact
            </span>
            <h2 className="font-heading text-3xl md:text-5xl font-semibold text-slate-900 mb-6">
              Impact Pillars at a Glance
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {impactPillars.map((pillar, index) => (
              <motion.div
                key={pillar.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="relative overflow-hidden bg-gradient-to-br from-slate-50 to-white border border-slate-200 p-8 group hover:border-teal-500 hover:shadow-lg transition-all"
                data-testid={`impact-pillar-${index}`}
              >
                <div className="absolute top-0 right-0 w-24 h-24 bg-teal-500/5 rounded-full -translate-y-1/2 translate-x-1/2 group-hover:bg-teal-500/10 transition-colors" />
                
                <div className="relative">
                  <div className="w-14 h-14 bg-teal-100 border border-teal-200 flex items-center justify-center mb-6">
                    <pillar.icon size={28} className="text-teal-600" />
                  </div>
                  <h3 className="font-heading text-2xl text-slate-900 mb-3">{pillar.title}</h3>
                  <p className="text-slate-500 leading-relaxed">{pillar.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Image Section */}
      <section className="section-padding bg-slate-50" data-testid="team-section">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <img
                src="https://images.unsplash.com/photo-1653566031535-bcf33e1c2893?w=800&q=75"
                alt="Black African Team in Business Meeting"
                className="w-full h-auto rounded-lg shadow-xl"
                loading="lazy"
                decoding="async"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="font-mono text-xs uppercase tracking-widest text-teal-600 mb-4 block">
                Our Team
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                African Excellence, Global Standards
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-6">
                Our team of African health policy experts brings deep local context 
                combined with world-class analytical capabilities. We understand the 
                unique challenges facing health systems across the continent.
              </p>
              <div className="grid grid-cols-3 gap-6">
                <div className="text-center">
                  <span className="font-heading text-3xl text-teal-600 block">Glocal</span>
                  <span className="text-slate-500 text-sm">Expertise</span>
                </div>
                <div className="text-center">
                  <span className="font-heading text-3xl text-teal-600 block">Rigorous</span>
                  <span className="text-slate-500 text-sm">Methodology</span>
                </div>
                <div className="text-center">
                  <span className="font-heading text-3xl text-teal-600 block">M&E</span>
                  <span className="text-slate-500 text-sm">Excellence</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-teal-600" data-testid="cta-section">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mb-4">
              Ready to Transform Your Health System?
            </h2>
            <p className="text-teal-100 text-lg mb-8 max-w-2xl mx-auto">
              Partner with HSL and architect the future of health excellence.
            </p>
            <Link
              to="/contact"
              className="bg-white text-teal-600 px-8 py-4 font-bold tracking-wide uppercase text-sm hover:bg-teal-50 transition-colors inline-flex items-center gap-2 group"
              data-testid="cta-partner"
            >
              Begin Your Journey
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
